/**
 * The portal Weekly Recap modal opens on its own at most once per recap week.
 *
 * The week is the one the RECAP describes: the API's `weekStart`
 * (WeeklyRecapResponseDto, TeamPortalService), the Monday 00:00 its counts
 * start from, sent as an ISO instant. The flag used to be keyed on the
 * browser's own ISO week, which turns over at the browser's midnight rather
 * than the API's:
 *  - east of UTC, early on Monday in India last week's recap showed again, and
 *    dismissing it then marked the new week as seen, hiding the new recap
 *    (audit 2026-09-25, T19);
 *  - an Indian manager of a Dubai workspace who closed the recap at 00:45 IST
 *    on Monday (still Sunday in Dubai, so last week's recap) closed the
 *    browser's new week, and the workspace's new recap never opened on its own
 *    (locale audit 2026-09-26).
 * The key is therefore that instant, as its UTC calendar date: every spelling
 * of the same Monday is the same week whatever zone the browser is in, and two
 * recap weeks, a week apart, never share a date.
 *
 * A recap without a readable `weekStart` (never sent by the API today) falls
 * back to the current UTC ISO week — never the browser's local week, which is
 * what went wrong above — so it still opens at most once a week.
 * Storage that throws (blocked site data, some private windows) reads as "not
 * closed" and ignores the write: the recap opens again next visit rather than
 * taking the tab down.
 */
const KEY_PREFIX = 'hf-portal:weekly-recap-dismissed:';

/** Monday 00:00 UTC of the ISO week containing `now`. */
function utcIsoWeekStart(now: Date): Date {
  const day = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
  return day;
}

/** The recap's own week; without one, the current UTC week (never local). */
function weekKey(weekStart?: string | null): string {
  const ms = typeof weekStart === 'string' ? Date.parse(weekStart) : Number.NaN;
  const start = Number.isFinite(ms) ? new Date(ms) : utcIsoWeekStart(new Date());
  return KEY_PREFIX + start.toISOString().slice(0, 10);
}

/** Whether the recap for the week starting `weekStart` was closed in this browser. */
export function isWeeklyRecapDismissed(weekStart?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(weekKey(weekStart));
  } catch {
    return false;
  }
}

/** Remember that the recap for the week starting `weekStart` was closed. */
export function dismissWeeklyRecap(weekStart?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(weekKey(weekStart), '1');
  } catch {
    // Storage refused: the recap simply opens again on the next visit.
  }
}
